import { Injectable, inject } from '@angular/core';
import { DialogService } from 'primeng/dynamicdialog';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  GR_CONFIRM_BREAKPOINTS,
  GR_CONFIRM_DEFAULT_WIDTH,
  GR_CONFIRM_TONE_ICONS,
  GrConfirmModalComponent,
  GrModalFooterComponent,
  type GrConfirmModalData,
  type GrConfirmOptions,
  type GrConfirmTone,
  type GrModalButton,
  type GrModalResponse,
} from '@components/overlays';

const CONFIRM_KEY = 'confirm';
const CANCEL_KEY = 'cancel';

@Injectable({ providedIn: 'root' })
export class GrConfirmService {
  private readonly dialogs = inject(DialogService);

  /**
   * Opens the shared confirmation dialog and emits once: `true` when the user confirmed, `false`
   * for cancel, the header ✕, or Escape. Completes with the dialog, so callers can pipe it
   * straight into the mutation they were about to run.
   */
  confirm(options: GrConfirmOptions): Observable<boolean> {
    const iconTone: GrConfirmTone =
      options.iconTone ?? (options.confirmVariant === 'danger' ? 'danger' : 'info');

    const data: GrConfirmModalData = {
      buttons: this.buttonsFor(options),
      message: options.message,
      description: options.description,
      icon: options.icon ?? GR_CONFIRM_TONE_ICONS[iconTone],
      iconTone,
    };

    const ref = this.dialogs.open(GrConfirmModalComponent, {
      header: options.title,
      width: options.width ?? GR_CONFIRM_DEFAULT_WIDTH,
      breakpoints: GR_CONFIRM_BREAKPOINTS,
      modal: true,
      closable: true,
      data,
      templates: { footer: GrModalFooterComponent },
    });

    // DialogService returns null when the app is being torn down; treat that as "not confirmed"
    // so callers never have to null-check before subscribing.
    if (!ref) return of(false);

    return ref.onClose.pipe(
      map((res?: GrModalResponse) => !!res && !res.cancelled && res.buttonKey === CONFIRM_KEY),
    );
  }

  private buttonsFor(options: GrConfirmOptions): GrModalButton[] {
    const confirmButton: GrModalButton = {
      key: CONFIRM_KEY,
      label: options.confirmLabel ?? (options.dismissOnly ? 'OK' : 'Confirm'),
      variant: options.confirmVariant ?? 'primary',
      leadingIcon: options.confirmIcon,
    };

    if (options.dismissOnly) return [confirmButton];

    return [
      {
        key: CANCEL_KEY,
        label: options.cancelLabel ?? 'Cancel',
        variant: 'secondary',
        cancel: true,
      },
      confirmButton,
    ];
  }
}
